// src/pages/Register.js
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  Link,
  Paper,
  Avatar,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  LocalLaundryService,
  Visibility,
  VisibilityOff,
  ArrowBack
} from '@mui/icons-material';

import { getData, saveData } from '../services/storageService';
// ✅ 1. ІМПОРТ I18N
import { useI18n } from '../i18n/i18n';

export default function Register() {
  const theme = useTheme();
  const navigate = useNavigate();
  // ✅ 2. ОТРИМАННЯ ФУНКЦІЇ 't'
  const { t } = useI18n();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('operator');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // ✅ 3. ПЕРЕКЛАД ВАЛІДАЦІЇ ТА ПОМИЛОК
    if (!username || !password || !confirmPassword) {
      setError(t('register.error_all_fields', 'Por favor, rellena todos los campos'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('register.error_password_mismatch', 'Las contraseñas no coinciden'));
      setLoading(false);
      return;
    }

    if (password.length < 4) {
      setError(t('register.error_password_length', 'La contraseña debe tener al menos 4 caracteres'));
      setLoading(false);
      return;
    }

    try {
      const users = await getData('users') || [];
      if (users.some(u => u.username.toLowerCase() === username.toLowerCase())) {
        setError(t('register.error_user_exists', 'El usuario ya existe'));
        setLoading(false);
        return;
      }

      const newUser = { 
        id: Date.now(), 
        username, 
        password, 
        role,
        createdAt: new Date().toISOString()
      };
      
      await saveData('users', [...users, newUser]);
      
      // Успішна реєстрація
      setTimeout(() => {
        alert(t('register.success_message', 'Registro exitoso. Ahora puedes iniciar sesión.'));
        navigate('/');
      }, 500);

    } catch (error) {
      console.error('Registration error:', error);
      setError(t('register.error_system', 'Error del sistema. Intente nuevamente.'));
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 3,
          maxWidth: 500,
          width: '100%',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* ✅ 4. ПЕРЕКЛАД КОМПОНЕНТІВ (JSX) */}
        
        {/* Заголовок та навігація */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ mr: 2, minWidth: 'auto' }}
          >
            {t('common_back', 'Volver')}
          </Button>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {t('register_link', 'Registrar Nuevo Usuario')}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              bgcolor: theme.palette.success.main,
              mx: 'auto',
              mb: 2
            }}
          >
            <PersonAddIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Typography variant="h6" color="text.secondary">
            {t('register.create_account_subtitle', 'Crear nueva cuenta de usuario')}
          </Typography>
        </Box>

        {/* Форма реєстрації */}
        <Box component="form" onSubmit={handleRegister} sx={{ mt: 1 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: 2
              }}
            >
              {error}
            </Alert>
          )}

          <TextField
            label={t('register.username_label', 'Nombre de usuario') + ' *'}
            variant="outlined"
            margin="normal"
            fullWidth
            required
            autoFocus
            value={username}
            onChange={e => setUsername(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            disabled={loading}
          />

          <TextField
            label={t('password_label', 'Contraseña') + ' *'}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  onClick={togglePasswordVisibility}
                  sx={{ minWidth: 'auto', p: 1 }}
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </Button>
              )
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            disabled={loading}
            helperText={t('register.password_helper', 'Mínimo 4 caracteres')}
          />

          <TextField
            label={t('register.confirm_password_label', 'Confirmar contraseña') + ' *'}
            type={showConfirmPassword ? 'text' : 'password'}
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  onClick={toggleConfirmPasswordVisibility}
                  sx={{ minWidth: 'auto', p: 1 }}
                  disabled={loading}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </Button>
              )
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            disabled={loading}
          />

          <FormControl fullWidth margin="normal" sx={{ mb: 3 }}>
            <InputLabel>{t('role_label', 'Rol del usuario') + ' *'}</InputLabel>
            <Select 
              value={role} 
              label={t('role_label', 'Rol del usuario') + ' *'}
              onChange={e => setRole(e.target.value)}
              sx={{ borderRadius: 2 }}
              disabled={loading}
            >
              <MenuItem value="operator">{t('role_operator', 'Operador')}</MenuItem>
              <MenuItem value="admin">{t('role_admin', 'Administrador')}</MenuItem>
              <MenuItem value="technician">{t('role_technician', 'Técnico')}</MenuItem>
              <MenuItem value="supervisor">{t('role_supervisor', 'Supervisor')}</MenuItem>
            </Select>
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            sx={{
              mt: 1,
              mb: 3,
              py: 1.5,
              borderRadius: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              backgroundColor: theme.palette.success.main,
              '&:hover': {
                backgroundColor: theme.palette.success.dark,
              }
            }}
            disabled={loading}
          >
            {loading ? t('register.loading', 'Registrando...') : t('register_button', 'Registrar Usuario')}
          </Button>

          <Typography variant="body2" align="center" color="text.secondary">
            {t('login_link_pre_text', '¿Ya tienes una cuenta?')}
            {' '}
            <Link 
              component={RouterLink} 
              to="/"
              sx={{ 
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {t('login_link', 'Iniciar sesión')}
            </Link>
          </Typography>
        </Box>

        {/* Інформація про ролі */}
        <Paper 
          variant="outlined" 
          sx={{ 
            mt: 3, 
            p: 2, 
            borderRadius: 2,
            backgroundColor: theme.palette.background.default
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            {t('register.roles_info_title', 'Información sobre roles:')}
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • <strong>{t('role_operator')}:</strong> {t('register.role_desc_operator', 'Registro de ciclos y operaciones diarias')}<br/>
            • <strong>{t('role_technician')}:</strong> {t('register.role_desc_technician', 'Mantenimiento y observaciones técnicas')}<br/>
            • <strong>{t('role_supervisor')}:</strong> {t('register.role_desc_supervisor', 'Supervisión y reportes')}<br/>
            • <strong>{t('role_admin')}:</strong> {t('register.role_desc_admin', 'Acceso completo al sistema')}
          </Typography>
        </Paper>
      </Paper>
    </Box>
  );
}